import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { 
  Calendar, 
  Download, 
  DollarSign, 
  TrendingUp, 
  FileText, 
  AlertTriangle,
  Calculator,
  PieChart,
  Receipt,
  BarChart3
} from 'lucide-react';
import { taxReportService } from '../../services/taxReportService';

// Format currency helper - outside components to avoid recreation
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount || 0);
};

// Get tax rate helper - outside components to avoid recreation
const getTaxRate = (income) => {
  if (income <= 10275) return 0.10;
  if (income <= 41775) return 0.12;
  if (income <= 89450) return 0.22;
  if (income <= 190750) return 0.24;
  if (income <= 364200) return 0.32;
  if (income <= 462550) return 0.35;
  return 0.37;
};

// Memoized TaxInsights component
const TaxInsights = memo(({ insights }) => {
  if (!insights) return null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6" key="insights-grid">
      <div className="bg-white rounded-lg shadow-sm border p-6" key="insights-panel">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <TrendingUp className="h-5 w-5 mr-2 text-green-600" />
          Tax Planning Insights
        </h3>
        
        <div className="space-y-4">
          {insights.recommendations && insights.recommendations.map((rec, index) => (
            <div key={`rec-${index}`} className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
              <p className="text-sm text-gray-700">{rec}</p>
            </div>
          ))}
        </div>
        
        {insights.estimatedTaxOwed && (
          <div className="mt-4 p-3 bg-yellow-50 rounded-md">
            <p className="text-sm font-medium text-yellow-800">
              Estimated Tax Owed: {formatCurrency(insights.estimatedTaxOwed)}
            </p>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-sm border p-6" key="alerts-panel">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <AlertTriangle className="h-5 w-5 mr-2 text-orange-600" />
          Compliance Alerts
        </h3>
        
        <div className="space-y-3">
          {insights.complianceAlerts && insights.complianceAlerts.map((alert, index) => (
            <div key={`alert-${index}`} className="flex items-start space-x-3 p-3 bg-orange-50 rounded-md">
              <AlertTriangle className="h-4 w-4 text-orange-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-orange-800">{alert}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
});

TaxInsights.displayName = 'TaxInsights';

// Memoized StatCard component
const StatCard = memo(({ title, value, icon: Icon, colorClass }) => (
  <div className="bg-white rounded-lg shadow-sm border p-4">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-gray-600">{title}</p>
        <p className={`text-2xl font-bold ${colorClass}`}>
          {value}
        </p>
      </div>
      <Icon className={`h-8 w-8 ${colorClass}`} />
    </div>
  </div>
));

StatCard.displayName = 'StatCard';

// Memoized TaxSummary component
const TaxSummary = memo(({ summary, taxCalculations }) => (
  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6" key="tax-summary">
    <StatCard 
      title="Total Income" 
      value={formatCurrency(summary?.totalIncome || 0)} 
      icon={DollarSign} 
      colorClass="text-green-600"
    />
    <StatCard 
      title="Total Expenses" 
      value={formatCurrency(summary?.totalExpenses || 0)} 
      icon={Receipt} 
      colorClass="text-red-600"
    />
    <StatCard 
      title="Net Income" 
      value={formatCurrency(summary?.netIncome || 0)} 
      icon={Calculator} 
      colorClass="text-blue-600"
    />
    <StatCard 
      title="Est. Tax Owed" 
      value={formatCurrency(taxCalculations?.estimatedTax || 0)} 
      icon={BarChart3} 
      colorClass="text-purple-600"
    />
  </div>
));

TaxSummary.displayName = 'TaxSummary';

// Memoized ExpenseCategories component
const ExpenseCategories = memo(({ expensesByCategory, totalExpenses, onExport, loading }) => (
  <div className="bg-white rounded-lg shadow-sm border p-6" key="expense-categories">
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-lg font-semibold flex items-center">
        <PieChart className="h-5 w-5 mr-2" />
        Business Expense Categories
      </h3>
      <div className="flex space-x-2">
        <button
          onClick={() => onExport('csv')}
          disabled={loading}
          className="bg-green-600 hover:bg-green-700 disabled:bg-green-300 text-white px-3 py-1 rounded text-sm flex items-center"
        >
          <Download className="h-4 w-4 mr-1" />
          CSV
        </button>
        <button
          onClick={() => onExport('json')}
          disabled={loading}
          className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white px-3 py-1 rounded text-sm flex items-center"
        >
          <Download className="h-4 w-4 mr-1" />
          JSON
        </button>
      </div>
    </div>

    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              IRS Category
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Amount
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Percentage
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {Object.entries(expensesByCategory || {}).map(([key, category]) => (
            <tr key={`category-${key}`}>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                {category.irsInfo?.description || 'Other'}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {formatCurrency(category.total || 0)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {((category.total / (totalExpenses || 1)) * 100).toFixed(1)}%
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
));

ExpenseCategories.displayName = 'ExpenseCategories';

// Memoized TaxCalculations component
const TaxCalculations = memo(({ taxCalculations }) => (
  <div className="bg-white rounded-lg shadow-sm border p-6" key="tax-calculations">
    <h3 className="text-lg font-semibold mb-4 flex items-center">
      <Calculator className="h-5 w-5 mr-2" />
      Tax Calculations
    </h3>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <h4 className="font-medium text-gray-900 mb-3">Self-Employment Tax</h4>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Net Earnings:</span>
            <span>{formatCurrency(taxCalculations?.netEarnings || 0)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">SE Tax Rate:</span>
            <span>15.3%</span>
          </div>
          <div className="flex justify-between border-t pt-2">
            <span className="text-gray-600">SE Tax:</span>
            <span className="font-semibold">{formatCurrency(taxCalculations?.selfEmploymentTax || 0)}</span>
          </div>
        </div>
      </div>

      <div>
        <h4 className="font-medium text-gray-900 mb-3">Income Tax</h4>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Taxable Income:</span>
            <span>{formatCurrency(taxCalculations?.taxableIncome || 0)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Tax Rate:</span>
            <span>{(getTaxRate(taxCalculations?.taxableIncome || 0) * 100).toFixed(1)}%</span>
          </div>
          <div className="flex justify-between border-t pt-2">
            <span className="text-gray-600">Income Tax:</span>
            <span className="font-semibold">{formatCurrency(taxCalculations?.incomeTax || 0)}</span>
          </div>
        </div>
      </div>
    </div>

    <div className="mt-6 p-4 bg-blue-50 rounded-md">
      <div className="flex justify-between items-center">
        <span className="text-lg font-semibold text-blue-900">Total Estimated Tax:</span>
        <span className="text-2xl font-bold text-blue-900">
          {formatCurrency(taxCalculations?.estimatedTax || 0)}
        </span>
      </div>
    </div>
  </div>
));

TaxCalculations.displayName = 'TaxCalculations';

// Memoized QuarterlyBreakdown component
const QuarterlyBreakdown = memo(({ quarterlyBreakdown }) => (
  <div className="bg-white rounded-lg shadow-sm border p-6 mb-6" key="quarterly-breakdown">
    <h3 className="text-lg font-semibold mb-4 flex items-center">
      <Calendar className="h-5 w-5 mr-2" />
      Quarterly Breakdown
    </h3>

    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {quarterlyBreakdown.map((quarter, index) => (
        <div key={`quarter-${quarter.quarter || index}`} className="border rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-2">{quarter.quarter}</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Revenue:</span>
              <span className="font-medium">{formatCurrency(quarter.income || 0)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Expenses:</span>
              <span className="font-medium">{formatCurrency(quarter.expenses || 0)}</span>
            </div>
            <div className="flex justify-between border-t pt-2">
              <span className="text-gray-600">Net Income:</span>
              <span className="font-semibold">{formatCurrency(quarter.netIncome || 0)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Est. Tax:</span>
              <span className="font-medium text-purple-600">
                {formatCurrency(quarter.estimatedTaxDue || 0)}
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
));

QuarterlyBreakdown.displayName = 'QuarterlyBreakdown';

// Memoized TaxReportData component
const TaxReportData = memo(({ taxData, onExport, loading }) => {
  if (!taxData) return null;
  
  // Use safe access with defaults
  const summary = taxData.summary || {};
  const taxCalculations = taxData.taxCalculations || {};
  const expensesByCategory = taxData.expensesByCategory || {};
  const quarterlyBreakdown = taxData.quarterlyBreakdown || [];
  
  return (
    <div key="tax-report-container">
      {/* Summary Statistics */}
      <TaxSummary summary={summary} taxCalculations={taxCalculations} />
      
      {/* Main Report Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6" key="report-grid">
        {/* Expense Categories */}
        {Object.keys(expensesByCategory).length > 0 && (
          <ExpenseCategories 
            expensesByCategory={expensesByCategory} 
            totalExpenses={summary.totalExpenses}
            onExport={onExport}
            loading={loading}
          />
        )}

        {/* Tax Calculations */}
        {taxCalculations && Object.keys(taxCalculations).length > 0 && (
          <TaxCalculations taxCalculations={taxCalculations} />
        )}
      </div>
      
      {/* Quarterly Breakdown */}
      {quarterlyBreakdown.length > 0 && (
        <QuarterlyBreakdown quarterlyBreakdown={quarterlyBreakdown} />
      )}
    </div>
  );
});

TaxReportData.displayName = 'TaxReportData';

// Main TaxReportingPage component
const TaxReportingPage = () => {
  const [loading, setLoading] = useState(false);
  const [taxData, setTaxData] = useState(null);
  const [insights, setInsights] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedQuarter, setSelectedQuarter] = useState(null);
  const [error, setError] = useState('');
  const [view, setView] = useState('insights'); // 'insights' or 'report'
  const [instanceId] = useState(() => `instance-${Date.now()}`); // Unique instance id for the component
  const [reportId, setReportId] = useState(Date.now()); // Unique ID for report instances

  const currentYear = new Date().getFullYear();
  const years = useMemo(() => Array.from({ length: 5 }, (_, i) => currentYear - i), [currentYear]);
  const quarters = useMemo(() => [
    { value: 1, label: 'Q1 (Jan-Mar)', period: 'Q1' },
    { value: 2, label: 'Q2 (Apr-Jun)', period: 'Q2' },
    { value: 3, label: 'Q3 (Jul-Sep)', period: 'Q3' },
    { value: 4, label: 'Q4 (Oct-Dec)', period: 'Q4' }
  ], []);

  // Define loadTaxInsights BEFORE the useEffect that uses it
  const loadTaxInsights = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await taxReportService.getTaxInsights();
      
      if (response && response.data) {
        setInsights(response.data);
        setView('insights');
      }
    } catch (error) {
      console.error('Error loading tax insights:', error);
      setError('Failed to load tax insights');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load insights on mount - now loadTaxInsights is defined above
  useEffect(() => {
    loadTaxInsights();
    
    // Cleanup function
    return () => {
      setTaxData(null);
      setInsights(null);
    };
  }, [loadTaxInsights]);

  const generateTaxReport = useCallback(async () => {
    try {
      // Reset state
      setLoading(true);
      setError('');
      setTaxData(null);
      
      const filters = {
        taxYear: selectedYear,
        ...(selectedQuarter && { quarter: selectedQuarter })
      };
      
      const response = await taxReportService.generateTaxReport(filters);
      
      if (response && response.data && response.data.taxReport) {
        setTaxData(response.data.taxReport);
        setView('report');
        setReportId(Date.now()); // Generate new report ID to force remounting
      } else {
        throw new Error('Invalid report data structure');
      }
    } catch (error) {
      console.error('Error generating tax report:', error);
      setError('Failed to generate tax report. Please try again.');
      setView('insights'); // Fall back to insights view on error
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedQuarter]);

  const exportTaxReport = useCallback(async (format) => {
    try {
      setLoading(true);
      const filters = {
        taxYear: selectedYear,
        ...(selectedQuarter && { quarter: selectedQuarter })
      };
      
      await taxReportService.exportTaxData(filters, format);
    } catch (error) {
      console.error('Error exporting tax report:', error);
      setError(`Failed to export tax report as ${format.toUpperCase()}`);
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedQuarter]);

  // Callbacks for form fields
  const handleYearChange = useCallback((e) => {
    setSelectedYear(parseInt(e.target.value));
  }, []);

  const handleQuarterChange = useCallback((e) => {
    setSelectedQuarter(e.target.value ? parseInt(e.target.value) : null);
  }, []);

  return (
    <div className="max-w-7xl mx-auto p-6 bg-gray-50 min-h-screen" key={instanceId}>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Tax Reporting & Planning</h1>
        <p className="text-gray-600">Generate IRS-compliant tax reports and get insights for your business</p>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
          <AlertTriangle className="h-5 w-5 text-red-500 mr-2" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          <Calendar className="h-5 w-5 mr-2" />
          Generate Tax Report
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tax Year
            </label>
            <select
              value={selectedYear}
              onChange={handleYearChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {years.map(year => (
                <option key={`year-${year}`} value={year}>{year}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Quarter (Optional)
            </label>
            <select
              value={selectedQuarter || ''}
              onChange={handleQuarterChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Full Year</option>
              {quarters.map(quarter => (
                <option key={`quarter-option-${quarter.value}`} value={quarter.value}>
                  {quarter.label}
                </option>
              ))}
            </select>
          </div>
          
          <div className="flex items-end">
            <button
              onClick={generateTaxReport}
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white py-2 px-4 rounded-md flex items-center justify-center"
              type="button"
              aria-label="Generate Report"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : (
                <span className="flex items-center">
                  <FileText className="h-4 w-4 mr-2 flex-shrink-0" aria-hidden="true" />
                  <span>Generate Report</span>
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Conditional rendering based on view state */}
      {view === 'insights' && insights && (
        <TaxInsights insights={insights} />
      )}

      {view === 'report' && taxData && (
        <TaxReportData 
          key={`tax-report-${reportId}`}
          taxData={taxData} 
          onExport={exportTaxReport}
          loading={loading}
        />
      )}
    </div>
  );
};

export default TaxReportingPage;
